import { LegendModifier, SciChartLegend } from './scichart';

/**
 * The vendor's own record shape — `IEventSubscriptionItem extends IDeletable`
 * (SciChartLegendBase.d.ts:15-19). Declared locally because our barrel does not
 * re-export it and reaching into a deep vendor path would bind us to their
 * directory layout. Note what the TYPE says: the record is deletable. The library
 * declares the disposer, stores it, and then never calls it.
 */
interface LegendSubscription {
  element?: HTMLElement;
  eventType?: string;
  eventListener?: EventListener;
  delete?: () => void;
}

/**
 * A LegendModifier whose legend actually releases what it subscribed to.
 *
 * ── THE VENDOR DEFECT, and it is the vendor's, not ours ──────────────────────
 *
 * On every rebuild the legend subscribes to each series' visibility event:
 *
 *     SciChartLegend.js:107    rs.isVisibleChanged.subscribe(visibilityChangeEventHandler_1)
 *
 * and it stores a disposer that would release BOTH that and the DOM listener:
 *
 *     SciChartLegend.js:112-115
 *         delete: function () {
 *             el.removeEventListener("change", onChangeEventListener_1);
 *             rs.isVisibleChanged.unsubscribe(visibilityChangeEventHandler_1);
 *         }
 *
 * But the only consumer of that record never calls it:
 *
 *     SciChartLegendBase.js:72-79
 *         removeEventListenerFromSeries = function (renderableSeriesId) {
 *             ...forEach(function (_a) {
 *                 var element = _a.element, eventListener = _a.eventListener,
 *                     eventType = _a.eventType;          // <- `delete` is dropped
 *                 element.removeEventListener(eventType, eventListener);
 *             });
 *
 * So the DOM listener goes and the series subscription stays, for the life of the
 * surface. It is NEW IN 5.2.69: `isVisibleChanged` appears zero times in every
 * 3.1.348 Legend file.
 *
 * ── WHAT IT COSTS, measured rather than asserted ─────────────────────────────
 *
 * A rebuild happens on any renderableSeries change AND on any visibility change —
 * so every time a user ticks a legend checkbox. On a seven-series surface, with a
 * live heap meter (control allocation +7.6 MB, forced GC between reads):
 *
 *     subscribers  7 -> 427 over 60 toggles   = +7.00 per toggle, one per series
 *     heap         1,137 KB over 60 toggles   ~ 2.8 KB per leaked subscription
 *
 * Roughly 19 KB per checkbox click, monotonic, never released while the chart
 * lives. Not an emergency in bytes; the sharper cost is that every leaked
 * subscriber calls invalidateLegend() on the next visibility change, so the work
 * done per click grows linearly with how long the screen has been open.
 *
 * ── WHY THIS SHAPE ───────────────────────────────────────────────────────────
 *
 * No vendor patching and no fork. `ILegendModifierOptions.legend` exists for
 * exactly this — "Set this only if you need to pass in a custom legend instance"
 * — and `removeEventListenerFromSeries` is an OWN PROPERTY assigned in the base
 * constructor, so it can be replaced on the instance rather than subclassed.
 *
 * The replacement calls the vendor's own `delete()` when there is one and falls
 * back to the vendor's exact behaviour when there is not, so a future SciChart
 * that fixes this upstream — or changes the record shape — keeps working and this
 * file quietly becomes a no-op.
 */
export function createLeakFreeLegendModifier(
  options: ConstructorParameters<typeof LegendModifier>[0] = {}
): LegendModifier {
  const legend = new SciChartLegend(options) as SciChartLegend &
    Record<string, unknown>;

  legend['removeEventListenerFromSeries'] = (renderableSeriesId: string) => {
    const collection = legend['eventListenersCollection'] as unknown as
      | Map<string, LegendSubscription[]>
      | undefined;
    collection?.get(renderableSeriesId)?.forEach((item) => {
      if (typeof item.delete === 'function') {
        // The vendor's own disposer: removes the DOM listener AND unsubscribes
        // from isVisibleChanged. This call is the entire fix.
        item.delete();
        return;
      }
      // No disposer on the record — do exactly what the vendor does, so an
      // upstream change of shape degrades to the current behaviour instead of
      // silently releasing nothing.
      if (item.element && item.eventType && item.eventListener) {
        item.element.removeEventListener(item.eventType, item.eventListener);
      }
    });
    collection?.delete(renderableSeriesId);
  };

  return new LegendModifier({ ...options, legend });
}
